import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";

// Lazy-load notifications so sonner/toast bundles are deferred
const LazyNotifications = lazy(() => import("./components/LazyNotifications"));

// Lazy-load ProtectedRoute/AdminRoute so supabase bundle is deferred from homepage
const ProtectedRoute = lazy(() => import("@/components/ProtectedRoute").then(m => ({ default: m.ProtectedRoute })));
const AdminRoute = lazy(() => import("@/components/ProtectedRoute").then(m => ({ default: m.AdminRoute })));

// Lazy-load auth layout so supabase bundle is deferred from homepage
const AuthLayout = lazy(() => import("./components/AuthLayout"));

// Lazy-load non-critical routes to reduce initial JS bundle
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminStats = lazy(() => import("./pages/admin/AdminStats"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminDeposits = lazy(() => import("./pages/admin/AdminDeposits"));
const AdminWithdrawals = lazy(() => import("./pages/admin/AdminWithdrawals"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminLogs = lazy(() => import("./pages/admin/AdminLogs"));
const AdminSupportTickets = lazy(() => import("./pages/admin/AdminSupportTickets"));
const AdminSupportChat = lazy(() => import("./pages/admin/AdminSupportChat"));
const AdminKYC = lazy(() => import("./pages/admin/AdminKYC"));
const AdminTradingCycles = lazy(() => import("./pages/admin/AdminTradingCycles"));
const AdminAnnouncements = lazy(() => import("./pages/admin/AdminAnnouncements"));
const AdminWalletConnections = lazy(() => import("./pages/admin/AdminWalletConnections"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Faq = lazy(() => import("./pages/Faq"));
const Cookies = lazy(() => import("./pages/Cookies"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const Install = lazy(() => import("./pages/Install"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <Suspense fallback={null}><LazyNotifications /></Suspense>
      <Suspense fallback={<div className="flex min-h-screen items-center justify-center bg-background"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>}>
        <Routes>
          {/* Public routes - no auth/supabase loaded */}
          <Route path="/" element={<Index />} />
          <Route path="/install" element={<Install />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/disclaimer" element={<Disclaimer />} />

          {/* Auth-required routes - supabase loaded on demand */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
              <Route index element={<AdminStats />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="deposits" element={<AdminDeposits />} />
              <Route path="withdrawals" element={<AdminWithdrawals />} />
              <Route path="trading-cycles" element={<AdminTradingCycles />} />
              <Route path="kyc" element={<AdminKYC />} />
              <Route path="support" element={<AdminSupportTickets />} />
              <Route path="chat" element={<AdminSupportChat />} />
              <Route path="announcements" element={<AdminAnnouncements />} />
              <Route path="wallets" element={<AdminWalletConnections />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="logs" element={<AdminLogs />} />
            </Route>
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
